import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/common/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import api, { getErrorMessage } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useBillingOptions, findOption } from "@/lib/billing";

const CARD_ANIM = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3 } };

const PLAN_NAME = { pro: "Pro Host", pm: "Property Manager" };
const fmtAmt = (n) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { options } = useBillingOptions();
  const intendedPlan = searchParams.get("plan"); // "pro" | "pm" | null
  const intendedInterval = searchParams.get("interval") || "monthly"; // monthly | 6mo | 12mo
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    trackEvent("signup_started", "/signup");
    // Recovery / prefill: support ?email= from an abandoned-signup recovery link.
    const prefill = searchParams.get("email");
    if (prefill) setEmail(prefill);
  }, [searchParams]);

  // Capture an in-progress signup so it can be recovered if abandoned.
  const captureLead = async () => {
    const value = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return;
    try {
      await api.post("/signup/start", { email: value });
    } catch (e) {
      /* non-blocking */
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (!agreed) {
      toast.error("Please agree to the Terms, Privacy Policy and Disclaimer to continue.");
      return;
    }
    setLoading(true);
    try {
      await signup(email, password, businessName);
      // If the user picked a paid plan, take them straight to Stripe Checkout.
      if (intendedPlan === "pro" || intendedPlan === "pm") {
        try {
          const res = await api.post("/checkout/session", {
            plan_id: intendedPlan,
            billing_interval: intendedInterval,
            origin_url: window.location.origin,
          });
          if (res.data?.url) {
            trackEvent("checkout_started", "/signup");
            window.location.href = res.data.url;
            return;
          }
        } catch (checkoutErr) {
          toast.error(getErrorMessage(checkoutErr, "Account created, but checkout couldn't start. You can upgrade from Pricing."));
          navigate("/app/pricing");
          return;
        }
      }
      toast.success("Account created. Welcome to HostGuard!");
      const pendingInvite = sessionStorage.getItem("hg_pending_invite");
      if (pendingInvite) {
        navigate(`/accept-invite?token=${pendingInvite}`);
        return;
      }
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(getErrorMessage(err, "Sign up failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 flex-col justify-between bg-primary p-12 text-primary-foreground lg:flex">
        <Logo dark />
        <div>
          <h2 className="font-display text-3xl font-semibold leading-tight">Protect your property with smarter booking risk checks.</h2>
          <p className="mt-4 max-w-md text-primary-foreground/70">Create your free account — 10 assessments every month, no card required.</p>
        </div>
        <p className="text-xs text-primary-foreground/50">No guest names. No blacklists. Just neutral, structured risk guidance.</p>
      </div>

      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <motion.div {...CARD_ANIM} className="w-full max-w-sm">
          <div className="mb-8 lg:hidden"><Logo /></div>
          <h1 className="font-display text-2xl font-semibold">Create your account</h1>
          <p className="mt-1 text-sm text-muted-foreground">Start assessing bookings in minutes.</p>

          {(intendedPlan === "pro" || intendedPlan === "pm") && (
            <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5 text-sm text-foreground" data-testid="signup-plan-notice">
              {(() => {
                const opt = findOption(options, intendedPlan, intendedInterval);
                const priceStr = opt ? `${fmtAmt(opt.amount)} ${opt.interval_label}` : "";
                return (
                  <>
                    You're signing up for <strong>{PLAN_NAME[intendedPlan]}{priceStr ? ` (${priceStr})` : ""}</strong>. After creating your account, you'll be taken to secure Stripe checkout.
                  </>
                );
              })()}
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="business">Business name <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="business" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="e.g. Coastal Stays Co." data-testid="signup-business-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} onBlur={captureLead} placeholder="you@example.com" data-testid="signup-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" data-testid="signup-password-input" />
            </div>
            <div className="flex items-start gap-2.5 pt-1">
              <Checkbox
                id="agree"
                checked={agreed}
                onCheckedChange={(v) => setAgreed(v === true)}
                className="mt-0.5"
                data-testid="signup-agree-checkbox"
              />
              <Label htmlFor="agree" className="text-xs font-normal leading-relaxed text-muted-foreground">
                I have read and agree to the{" "}
                <Link to="/terms" target="_blank" className="font-medium text-primary hover:underline">Terms of Service</Link>,{" "}
                <Link to="/privacy" target="_blank" className="font-medium text-primary hover:underline">Privacy Policy</Link>{" "}
                and{" "}
                <Link to="/disclaimer" target="_blank" className="font-medium text-primary hover:underline">Risk Assessment Disclaimer</Link>.
              </Label>
            </div>
            <Button type="submit" className="w-full" disabled={loading || !agreed} data-testid="signup-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create free account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account? <Link to={`/login${location.search}`} className="font-medium text-primary hover:underline" data-testid="signup-to-login-link">Log in</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
